import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Heading,
  VStack,
} from '@chakra-ui/react';
import type { RiftboundFilter } from '../types';
import { emptyFilter } from '../types';
import { useFacets } from '../hooks/useFacets';
import { SUPERTYPES, setIsPreviewOrUpcoming } from '../constants';
import { countActiveFilters } from '../utils/cardUtils';
import { setDisplayName } from './collection/collectionStatsUtils';
import { SearchAutocomplete, type FacetCategory } from './SearchAutocomplete';
import { DomainFilter } from './DomainFilter';
import { RarityFilter } from './RarityFilter';
import { VariantFilter } from './VariantFilter';
import { NumberRangeSlider } from './NumberRangeSlider';
import { KeywordFilter } from './KeywordFilter';
import { PillToggleFilter } from './ui/pill-toggle-filter';
import { FilterGroup } from './FilterGroup';
import { SetLogoThumb } from './set/SetLogoThumb';
import { RiftboundCheckbox } from './ui/checkbox';
import { useInFilterSheet, useReportMobileFilterCount } from './layout/MobileFilterSheetContext';
import { useClaimTabletHeaderSearch, useHeaderSearchSlot, useTabletHeaderSearch } from './layout/HeaderSearchSlot';
import { useSidebarResetSlot } from './layout/SidebarResetSlot';
import { PriceRangeFilter } from './market/PriceRangeFilter';

interface Props {
  filter: RiftboundFilter;
  setFilter: (f: RiftboundFilter) => void;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  /** Hide the set checklist when the parent route already fixes the active set. */
  hideSetFilter?: boolean;
  /** Called when a suggestion/history entry is selected — commits immediately without debounce. */
  onCommitSearch?: (term: string) => void;
  /** Called when a facet pill is selected from the autocomplete dropdown. */
  onCommitFacet?: (category: FacetCategory, value: string) => void;
  /** Optional block rendered above the Search field (e.g. team “Owned by” pills). */
  leading?: ReactNode;
  /**
   * Extra facets outside this sidebar (e.g. collection “holes only”) that should
   * count toward the mobile filter FAB badge.
   */
  extraActiveFilterCount?: number;
}

function toggle<T extends string>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

const COST_MAX = 10;
const MIGHT_MAX = 10;
const POWER_MIN = 0;
const POWER_MAX = 4;

export function SearchSidebar({
  filter,
  setFilter,
  searchTerm,
  setSearchTerm,
  hideSetFilter = false,
  onCommitSearch,
  onCommitFacet,
  leading,
  extraActiveFilterCount = 0,
}: Props) {
  const facets = useFacets();
  const inSheet = useInFilterSheet();
  const headerSearchSlot = useHeaderSearchSlot();
  const tabletHeaderSearch = useTabletHeaderSearch();
  const searchInHeader = tabletHeaderSearch && !inSheet && headerSearchSlot != null;
  useClaimTabletHeaderSearch(searchInHeader);
  const resetSlot = useSidebarResetSlot();
  const pinReset = !inSheet && resetSlot.enabled;
  const activeFilterCount =
    countActiveFilters(filter, { ignoreSet: hideSetFilter }) + extraActiveFilterCount;
  const hasActiveFilters = activeFilterCount > 0 || searchTerm.trim().length > 0;
  useReportMobileFilterCount(activeFilterCount);

  const resetButton = hasActiveFilters ? (
    <Button
      variant="outline"
      size="sm"
      w={inSheet || pinReset ? 'full' : undefined}
      minH={inSheet ? '44px' : undefined}
      bg={pinReset ? 'bgElevated' : undefined}
      boxShadow={pinReset ? 'md' : undefined}
      onClick={() => {
        setFilter(emptyFilter);
        setSearchTerm('');
      }}
    >
      Reset filters
    </Button>
  ) : null;

  const searchField = (
    <SearchAutocomplete
      value={searchTerm}
      onChange={setSearchTerm}
      onCommit={onCommitSearch}
      onCommitFacet={onCommitFacet}
      facets={{
        keywords: facets.keywords,
        cardTypes: facets.cardTypes,
        supertypes: facets.supertypes,
        regions: facets.regions,
        subtypes: facets.subtypes,
        rarities: facets.rarities,
        artists: facets.artists,
      }}
    />
  );

  const filterFields = (
    <>
      {leading}
      {searchInHeader && headerSearchSlot ? (
        createPortal(searchField, headerSearchSlot)
      ) : (
        <Box display={{ base: 'none', md: 'block' }}>
          <Heading as="h2" size="sm" mb={2}>
            Search
          </Heading>
          {searchField}
        </Box>
      )}

      <PillToggleFilter
        label="Card type"
        options={facets.cardTypes}
        selected={filter.cardType ?? []}
        onToggle={(v) => setFilter({ ...filter, cardType: toggle(filter.cardType ?? [], v) })}
        onClearSelected={() => setFilter({ ...filter, cardType: [] })}
      />

      <PillToggleFilter
        label="Supertype"
        options={[...SUPERTYPES]}
        selected={filter.supertype ?? []}
        onToggle={(v) => setFilter({ ...filter, supertype: toggle(filter.supertype ?? [], v) })}
        onClearSelected={() => setFilter({ ...filter, supertype: [] })}
      />

      <DomainFilter
        selected={filter.domain ?? []}
        onChange={(v) => setFilter({ ...filter, domain: toggle(filter.domain ?? [], v) })}
      />

      <RarityFilter
        selected={filter.rarity ?? []}
        onChange={(v) => setFilter({ ...filter, rarity: toggle(filter.rarity ?? [], v) })}
      />

      <VariantFilter
        selected={filter.variant ?? []}
        onChange={(v) => setFilter({ ...filter, variant: toggle(filter.variant ?? [], v) })}
      />

      {!hideSetFilter && (
        <FilterGroup
          title="Set"
          options={facets.sets}
          selected={filter.set}
          onToggle={(v) => setFilter({ ...filter, set: toggle(filter.set ?? [], v) })}
          formatOptionLabel={(code) => {
            const name = setDisplayName(code, facets.setNames);
            return setIsPreviewOrUpcoming(code) ? `${name} (Preview)` : name;
          }}
          renderLeading={(code) => (
            <SetLogoThumb code={code} name={setDisplayName(code, facets.setNames)} />
          )}
        />
      )}

      {facets.champions.length > 0 && (
        <FilterGroup
          title="Champion"
          options={facets.champions}
          selected={filter.champion}
          onToggle={(v) => setFilter({ ...filter, champion: toggle(filter.champion ?? [], v) })}
          collapsible
        />
      )}

      <KeywordFilter
        keywords={facets.keywords}
        selected={filter.keyword ?? []}
        onChange={(v) => setFilter({ ...filter, keyword: toggle(filter.keyword ?? [], v) })}
        onClearSelected={() => setFilter({ ...filter, keyword: [] })}
      />

      <NumberRangeSlider
        label="Energy"
        min={0}
        max={COST_MAX}
        selected={filter.cost ?? []}
        onChange={(vals) => setFilter({ ...filter, cost: vals })}
      />

      <NumberRangeSlider
        label="Might"
        min={0}
        max={MIGHT_MAX}
        selected={filter.might ?? []}
        onChange={(vals) => setFilter({ ...filter, might: vals })}
      />

      <NumberRangeSlider
        label="Power"
        min={POWER_MIN}
        max={POWER_MAX}
        selected={filter.power ?? []}
        onChange={(vals) => setFilter({ ...filter, power: vals })}
      />

      <PriceRangeFilter
        priceMin={filter.priceMin}
        priceMax={filter.priceMax}
        onChange={({ priceMin, priceMax }) => setFilter({ ...filter, priceMin, priceMax })}
      />

      {facets.subtypes.length > 0 && (
        <KeywordFilter
          label="Subtype"
          keywords={facets.subtypes}
          selected={filter.subtype ?? []}
          onChange={(v) => setFilter({ ...filter, subtype: toggle(filter.subtype ?? [], v) })}
          onClearSelected={() => setFilter({ ...filter, subtype: [] })}
          searchPlaceholder="Filter subtypes…"
          emptySearchMessage="No subtypes match."
        />
      )}

      <PillToggleFilter
        label="Region"
        options={facets.regions}
        selected={filter.region ?? []}
        onToggle={(v) => setFilter({ ...filter, region: toggle(filter.region ?? [], v) })}
        onClearSelected={() => setFilter({ ...filter, region: [] })}
      />

      {facets.artists.length > 0 && (
        <KeywordFilter
          label="Artist"
          keywords={facets.artists}
          selected={filter.artist ?? []}
          onChange={(v) => setFilter({ ...filter, artist: toggle(filter.artist ?? [], v) })}
          onClearSelected={() => setFilter({ ...filter, artist: [] })}
          searchPlaceholder="Filter artists…"
          emptySearchMessage="No artists match."
          maxRows={2}
        />
      )}

      <RiftboundCheckbox
        checked={Boolean(filter.includePreviewed)}
        onCheckedChange={(d) => setFilter({ ...filter, includePreviewed: Boolean(d.checked) })}
        w={inSheet ? 'full' : undefined}
        minH={inSheet ? '44px' : undefined}
        alignItems="center"
      >
        Include previewed / unreleased cards
      </RiftboundCheckbox>
    </>
  );

  if (inSheet) {
    return (
      <VStack align="stretch" gap={8} px={3} py={3} bg="transparent" borderRadius="none">
        {filterFields}
        {resetButton}
      </VStack>
    );
  }

  const filterCard = (
    <VStack align="stretch" gap={5} px={4} py={4} bg="bgElevated" borderRadius="lg">
      {filterFields}
      {!pinReset && resetButton}
    </VStack>
  );

  if (pinReset && resetSlot.element) {
    return (
      <>
        {filterCard}
        {createPortal(resetButton, resetSlot.element)}
      </>
    );
  }

  return filterCard;
}

/** Alias used by collection-specific sidebar composition. */
export { SearchSidebar as CatalogSidebarFilters };
